import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Flame, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { TrendingCard, LatestCard } from '../components/RailCards';
import { AddToPlaylistModal } from '../components/AddToPlaylistModal';
import { shareVideo } from '../utils/share';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';

/**
 * Shared destination page for /discover/trending and /discover/latest.
 *
 * Spec (Feb 2026):
 *  - DO NOT modify the Home screen. This page is reached from the "View All ›"
 *    buttons on Home's Trending Now / Latest Uploads rails.
 *  - Reuse the EXACT same rail card components — same portrait thumbnail,
 *    same share/queue icons, same like, same NEW badge, same truncated
 *    title/creator treatment. No new wider or landscape card.
 *  - Responsive 2-up grid on phone, 3-up on tablet, more on desktop.
 *    Tapping "View All" should feel like "the same shelf expanded".
 *  - Titles match the rail headers verbatim: "Trending Now" (with flame
 *    icon) and "Latest Uploads".
 *  - Creator-diversity cap (≤2 per creator on first page) is enforced
 *    server-side in /api/discover/trending and /api/discover/latest, so the
 *    grid here just renders whatever the API returns.
 */
const DiscoverListingPage = ({ variant }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playlistVideoId, setPlaylistVideoId] = useState(null);
  const { axiosInstance, user } = useAuth();
  const { playVideo } = usePlayer();
  const navigate = useNavigate();

  const config = useMemo(() => {
    if (variant === 'trending') {
      return {
        endpoint: '/discover/trending',
        title: 'Trending Now',
        Icon: Flame,
        documentTitle: 'Trending Now — AUDIVUE',
        emptyHeading: 'Nothing trending yet',
        emptyBody: "As people watch and like videos, they'll show up here.",
        testid: 'trending-listing',
      };
    }
    return {
      endpoint: '/discover/latest',
      title: 'Latest Uploads',
      Icon: null,
      documentTitle: 'Latest Uploads — AUDIVUE',
      emptyHeading: 'No videos published yet',
      emptyBody: 'New uploads land here the moment they go live.',
      testid: 'latest-listing',
    };
  }, [variant]);

  useEffect(() => {
    const prev = document.title;
    document.title = config.documentTitle;
    return () => { document.title = prev; };
  }, [config.documentTitle]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(config.endpoint, { params: { limit: 40 } });
      setVideos(Array.isArray(res.data) ? res.data : (res.data?.videos || []));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch ${config.endpoint}:`, err);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.endpoint]);

  // Same callbacks the Home rails pass into TrendingCard so the action
  // affordances behave identically (Share = native share or copy link,
  // Queue = AddToPlaylist modal, Like = POST /favorites).
  const handleLikeVideo = async (videoId, newLiked) => {
    if (!user) {
      toast.error('Please sign in to like videos');
      return;
    }
    try {
      if (newLiked) {
        await axiosInstance.post(`/favorites/${videoId}`);
      } else {
        await axiosInstance.delete(`/favorites/${videoId}`);
      }
    } catch (_e) {
      toast.error('Failed to update favorites');
    }
  };

  const handleShare = (video) => shareVideo(video);
  const handlePlaylist = (video) => {
    if (!user) {
      toast.error('Please sign in to add to playlist');
      return;
    }
    setPlaylistVideoId(video.id);
  };
  const goToArtist = (video) => {
    const artistId = video.artist_id || video.user_id;
    if (artistId) navigate(`/creator/${artistId}`);
  };
  const getThumbnail = (video) => resolveThumbnailUrl(video);

  // Pick the right rail card per variant. Trending = full TrendingCard (with
  // share/queue/like). Latest = compact LatestCard (NEW badge, title/creator).
  const Card = variant === 'trending' ? TrendingCard : LatestCard;
  const cardProps = (video, index) => {
    const base = {
      video,
      thumbnail: getThumbnail(video),
      onClick: () => playVideo(video, videos, index),
    };
    if (variant === 'trending') {
      return {
        ...base,
        onArtistClick: goToArtist,
        onLike: handleLikeVideo,
        onPlaylist: handlePlaylist,
        onShare: handleShare,
      };
    }
    return base;
  };

  return (
    <div className="pb-32" data-testid={config.testid}>
      {/* Header — matches the Home rail header style verbatim (sans bold) so
          the destination feels like the same shelf expanded. Flame icon for
          Trending Now (same icon + same orange tint as the rail). */}
      <section className="px-4 pt-6 pb-2">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4 transition-colors"
          data-testid={`${config.testid}-back`}
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Home
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-between"
        >
          <h1
            className="text-xl font-bold text-white flex items-center gap-2"
            data-testid={`${config.testid}-h1`}
          >
            {config.Icon && <config.Icon className="w-5 h-5 text-orange-400" />}
            {config.title}
          </h1>
          <button
            onClick={fetchVideos}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid={`${config.testid}-refresh`}
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </motion.div>
      </section>

          {/* Grid — 2-up phone (always), 3-up tablet (md:768px+), 4-up
          laptop (lg:1024px+), 5-up wide desktop (xl:1280px+). Per spec the
          phone is locked to 2-up. The portrait rail cards have fixed widths
          internally (w-[150px]/w-[105px]) so we place them inside
          `justify-items-center` cells to keep the grid tidy without forcing
          the cards to stretch into a new "landscape" shape. */}
      <section className="px-4">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 justify-items-center">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={`sk-${i}`}
                className={`w-full ${variant === 'trending' ? 'max-w-[165px]' : 'max-w-[115px]'} aspect-[9/16] bg-gray-800 rounded-xl animate-pulse`}
              />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20" data-testid={`${config.testid}-empty`}>
            {config.Icon && <config.Icon className="w-16 h-16 mx-auto mb-4 text-orange-400 opacity-50" />}
            <h3 className="text-xl text-zinc-300 mb-2">{config.emptyHeading}</h3>
            <p className="text-zinc-500 max-w-sm mx-auto">{config.emptyBody}</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 justify-items-center"
            data-testid={`${config.testid}-grid`}
          >
            {videos.map((video, index) => (
              <Card key={video.id} {...cardProps(video, index)} />
            ))}
          </div>
        )}
      </section>

      <AddToPlaylistModal
        isOpen={!!playlistVideoId}
        onClose={() => setPlaylistVideoId(null)}
        videoId={playlistVideoId}
      />
    </div>
  );
};

export const TrendingVideosPage = () => <DiscoverListingPage variant="trending" />;
export const LatestVideosPage = () => <DiscoverListingPage variant="latest" />;

export default DiscoverListingPage;
